import { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ReplayIcon from '@material-ui/icons/Replay';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import SyncIcon from '@material-ui/icons/Sync';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { serviceRollbackPermission } from '../../permissions';

interface EnvConfig {
  key: string;
  displayName: string;
  color: string;
}

interface HistoryEntry {
  id: number;
  revision?: string;
  revisions?: string[];
  deployedAt: string;
  deployStartedAt?: string;
  sources?: Array<{ repoURL?: string; path?: string }>;
  source?: { repoURL?: string };
}

interface AppData {
  spec?: {
    syncPolicy?: {
      automated?: object | null;
    };
    source?: { repoURL?: string; targetRevision?: string; [key: string]: any };
    sources?: Array<{ repoURL?: string; targetRevision?: string; [key: string]: any }>;
  };
  status?: {
    history?: HistoryEntry[];
    operationState?: {
      finishedAt?: string;
      syncResult?: { revision?: string };
    };
    health?: { status?: string };
    sync?: { status?: string; revision?: string; revisions?: string[] };
  };
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      padding: theme.spacing(1.2, 2),
    },
  },
  commitHash: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  date: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  currentRow: {
    background: theme.palette.action.selected,
  },
  autoSyncBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    background: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.warning.contrastText,
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

function getHealthColor(status?: string): string {
  if (status === 'Healthy') return '#4caf50';
  if (status === 'Degraded' || status === 'Missing') return '#f44336';
  if (status === 'Progressing') return '#2196f3';
  return '#9e9e9e';
}

function getSyncColor(status?: string): string {
  if (status === 'Synced') return '#4caf50';
  if (status === 'OutOfSync') return '#ff9800';
  return '#9e9e9e';
}

function HealthIcon({ status }: { status?: string }) {
  if (status === 'Healthy')
    return <CheckCircleIcon fontSize="small" style={{ color: '#4caf50' }} />;
  if (status === 'Degraded')
    return <ErrorIcon fontSize="small" style={{ color: '#f44336' }} />;
  return <SyncIcon fontSize="small" style={{ color: '#2196f3' }} />;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days}j`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export const ArgocdDeploymentsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const argoPrefix = annotations['fondative.io/argocd-app-prefix'];
  const legacyAppName = annotations['argocd/app-name'];

  const [activeTab, setActiveTab] = useState(0);
  const [envs, setEnvs] = useState<EnvConfig[]>([]);
  const [appDataMap, setAppDataMap] = useState<Record<string, AppData | null>>({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [rolling, setRolling] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<HistoryEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enablingAutoSync, setEnablingAutoSync] = useState(false);
  const { allowed: canRollback } = usePermission({ permission: serviceRollbackPermission });

  // Read environments from config
  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          color: c.getOptionalString('color') ?? '#1976d2',
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  const getAppName = useCallback(
    (envKey: string) => (argoPrefix ? `${argoPrefix}-${envKey}` : legacyAppName ?? ''),
    [argoPrefix, legacyAppName],
  );

  // Fetch ArgoCD app data per environment
  const fetchAppForEnv = useCallback(
    async (envKey: string) => {
      const appName = getAppName(envKey);
      if (!appName) return;
      setLoadingMap(prev => ({ ...prev, [envKey]: true }));
      try {
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/proxy/argocd/api/applications/${appName}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setAppDataMap(prev => ({ ...prev, [envKey]: data }));
      } catch {
        setAppDataMap(prev => ({ ...prev, [envKey]: null }));
      } finally {
        setLoadingMap(prev => ({ ...prev, [envKey]: false }));
      }
    },
    [backendBaseUrl, fetchApi, getAppName],
  );

  useEffect(() => {
    if (!envs.length) return;
    envs.forEach(env => fetchAppForEnv(env.key));
  }, [envs, fetchAppForEnv]);

  const handleRollback = useCallback(
    async (entry: HistoryEntry, envKey: string) => {
      const appName = getAppName(envKey);
      if (!appName) return;
      setConfirm(null);
      setRolling(entry.id);
      setError(null);
      try {
        const appRes = await fetchApi.fetch(
          `${backendBaseUrl}/api/proxy/argocd/api/applications/${appName}`,
        );
        if (!appRes.ok) throw new Error(`Fetch HTTP ${appRes.status}`);
        const currentApp = await appRes.json();

        const disablePatch = entry.revisions
          ? {
              spec: {
                syncPolicy: { automated: null },
                sources: (currentApp.spec?.sources ?? []).map((src: any, i: number) => ({
                  ...src,
                  targetRevision: entry.revisions![i] ?? entry.revisions![0],
                })),
              },
            }
          : {
              spec: {
                syncPolicy: { automated: null },
                source: { ...currentApp.spec?.source, targetRevision: entry.revision },
              },
            };

        const patchRes = await fetchApi.fetch(
          `${backendBaseUrl}/api/proxy/argocd/api/applications/${appName}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ patch: JSON.stringify(disablePatch), patchType: 'merge' }),
          },
        );
        if (!patchRes.ok) {
          const body = await patchRes.json().catch(() => ({}));
          throw new Error(body.message || `Patch HTTP ${patchRes.status}`);
        }

        const syncRes = await fetchApi.fetch(
          `${backendBaseUrl}/api/proxy/argocd/api/applications/${appName}/sync`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prune: false, dryRun: false }),
          },
        );
        if (!syncRes.ok) {
          const body = await syncRes.json().catch(() => ({}));
          throw new Error(body.message || `Sync HTTP ${syncRes.status}`);
        }
        setTimeout(() => fetchAppForEnv(envKey), 3000);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setRolling(null);
      }
    },
    [backendBaseUrl, fetchApi, getAppName, fetchAppForEnv],
  );

  const handleEnableAutoSync = useCallback(
    async (envKey: string) => {
      const appName = getAppName(envKey);
      const appData = appDataMap[envKey];
      if (!appName || !appData) return;
      setEnablingAutoSync(true);
      setError(null);
      try {
        const patchSpec: any = {
          syncPolicy: { automated: { prune: false, selfHeal: true } },
        };
        if (appData.spec?.sources) {
          patchSpec.sources = appData.spec.sources.map((src: any) => ({
            ...src,
            targetRevision: 'HEAD',
          }));
        } else if (appData.spec?.source) {
          patchSpec.source = { ...appData.spec.source, targetRevision: 'HEAD' };
        }
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/proxy/argocd/api/applications/${appName}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ patch: JSON.stringify({ spec: patchSpec }), patchType: 'merge' }),
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `HTTP ${res.status}`);
        }
        await fetchAppForEnv(envKey);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setEnablingAutoSync(false);
      }
    },
    [appDataMap, backendBaseUrl, fetchApi, getAppName, fetchAppForEnv],
  );

  if (!argoPrefix && !legacyAppName) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          Ajoutez l'annotation <code>fondative.io/argocd-app-prefix</code> pour activer la vue Deployments.
        </Typography>
      </Box>
    );
  }

  if (!envs.length) return null;

  const currentEnv = envs[activeTab];
  const appData = appDataMap[currentEnv.key];
  const isLoading = loadingMap[currentEnv.key] ?? true;
  const history = [...(appData?.status?.history ?? [])].reverse();
  const currentRevision =
    appData?.status?.sync?.revision ?? appData?.status?.sync?.revisions?.[0];
  const health = appData?.status?.health?.status;
  const syncStatus = appData?.status?.sync?.status;
  const autoSyncEnabled = !!appData?.spec?.syncPolicy?.automated;
  const appName = getAppName(currentEnv.key);

  return (
    <>
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          {envs.map(env => (
            <Tab
              key={env.key}
              label={env.displayName}
              style={{ color: env.color }}
            />
          ))}
        </Tabs>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : !appData ? (
          <Box className={classes.empty}>
            <Typography color="textSecondary">
              Application ArgoCD <strong>{appName}</strong> non trouvée
            </Typography>
          </Box>
        ) : (
          <>
            {/* Status bar */}
            <Box className={classes.statusBar}>
              <HealthIcon status={health} />
              <Chip
                size="small"
                label={health ?? '?'}
                style={{
                  backgroundColor: getHealthColor(health),
                  color: '#fff',
                  fontSize: '0.65rem',
                  height: 20,
                  fontWeight: 600,
                }}
              />
              <Chip
                size="small"
                label={syncStatus ?? '?'}
                style={{
                  backgroundColor: getSyncColor(syncStatus),
                  color: '#fff',
                  fontSize: '0.65rem',
                  height: 20,
                  fontWeight: 600,
                }}
              />
              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
                {appName}
              </Typography>
            </Box>

            {error && (
              <Box p={2}>
                <Typography color="error" variant="body2">{error}</Typography>
              </Box>
            )}

            {!autoSyncEnabled && (
              <Box className={classes.autoSyncBanner} m={2}>
                <SyncDisabledIcon fontSize="small" />
                <Typography variant="body2" style={{ flex: 1 }}>
                  Auto-sync désactivé
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  disabled={enablingAutoSync}
                  startIcon={enablingAutoSync ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
                  onClick={() => handleEnableAutoSync(currentEnv.key)}
                >
                  Réactiver
                </Button>
              </Box>
            )}

            <CardContent style={{ padding: 0 }}>
              {history.length === 0 ? (
                <Box className={classes.empty}>
                  <Typography color="textSecondary">
                    Aucun historique de déploiement
                  </Typography>
                </Box>
              ) : (
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Révision</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map(entry => {
                      const entryRevision = entry.revision ?? entry.revisions?.[0];
                      const isCurrent = !!entryRevision && entryRevision === currentRevision;
                      return (
                        <TableRow
                          key={entry.id}
                          hover
                          className={isCurrent ? classes.currentRow : undefined}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                              <span className={classes.commitHash}>
                                {(entryRevision ?? '?').slice(0, 8)}
                              </span>
                              {isCurrent && (
                                <Chip
                                  label="actuel"
                                  size="small"
                                  style={{
                                    backgroundColor: '#4caf50',
                                    color: '#fff',
                                    fontSize: '0.6rem',
                                    height: 18,
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={isCurrent ? 'Deployed' : `#${entry.id}`}
                              style={{
                                backgroundColor: isCurrent ? '#4caf50' : '#9e9e9e',
                                color: '#fff',
                                fontSize: '0.65rem',
                                height: 20,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography className={classes.date}>
                              {formatDate(entry.deployedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {!isCurrent && canRollback && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={
                                  rolling === entry.id ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <ReplayIcon />
                                  )
                                }
                                disabled={rolling !== null}
                                onClick={() => setConfirm(entry)}
                                style={{ fontSize: '0.7rem', textTransform: 'none' }}
                              >
                                Rollback
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </>
        )}
      </Card>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>Confirmer le rollback</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revenir à la révision{' '}
            <strong>{(confirm?.revision ?? confirm?.revisions?.[0] ?? '?').slice(0, 8)}</strong> déployée le{' '}
            {confirm && new Date(confirm.deployedAt ?? '').toLocaleString('fr-FR')} ?
            <br />
            <br />
            L'auto-sync sera désactivé et ArgoCD synchronisera l'application vers cette révision.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Annuler</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => confirm && handleRollback(confirm, currentEnv.key)}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
